import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetExpenses, AddExpense, UpdateExpense, GetExpensePayments, AddExpensePayment, UpdateExpensePayment,
  GetFunctionsByEventId, GetGuestsByEventId, GetCardTypes,
} from '../../api/api';
import { normaliseText, todayISO, tenDigits, EXPENSE_CATEGORIES } from '../../utils/helpers';
import { functionHeadcount } from '../../utils/headcount';

// Expenses of an event — all logic, shared by Expenses.desktop / .mobile.
//   An expense is Fixed (a typed amount) or PerPerson (rate × persons, the
//   persons typed or taken LIVE from a function's headcount, extra-card guests
//   included). It stays an Estimate until the final amount is known.
//   Payments (date, amount, paid by, mode) are kept per expense; Paid comes from
//   the API (sum of payments), Balance = total − paid.
// Rights: admins always; members by CanExpenses / Add / Edit / Delete.

const blankExpense = () => ({
  Category: '', Title: '', FunctionId: '', VendorName: '', VendorMobile: '',
  AmountType: 'Fixed', Rate: '', Persons: '', PersonsSource: 'Manual', PersonsFunctionId: '',
  TotalAmount: '', IsEstimate: 1, Notes: '',
});
const blankPayment = (ExpenseId, PaidBy) => ({
  ExpenseId, PaymentDate: todayISO(), Amount: '', PaidBy: PaidBy || '', PaymentMode: 'Cash', Notes: '',
});
const num = (v) => Number(v || 0) || 0;
const round2 = (n) => Math.round(n * 100) / 100;

export function useExpenses(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;
  const myName = [user.Name, user.Surname].filter(Boolean).join(' ');

  const canView = isAdmin || can('CanExpenses');
  const canAdd = isAdmin || can('CanExpensesAdd');
  const canEdit = isAdmin || can('CanExpensesEdit');
  const canDelete = isAdmin || can('CanExpensesDelete');

  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  const [openId, setOpenId] = useState(null);       // expense whose payments are shown
  const [form, setForm] = useState(null);           // expense being added / edited
  const [payForm, setPayForm] = useState(null);     // payment being added / edited
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [e, p] = await Promise.all([GetExpenses({ EventId: event.EventId }), GetExpensePayments({ EventId: event.EventId })]);
      setExpenses(Array.isArray(e) ? e : []);
      setPayments(Array.isArray(p) ? p : []);
    } catch {
      toast('Could not load expenses');
    } finally {
      setLoading(false);
    }
  }, [event, canView, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canView) return;
    GetFunctionsByEventId({ EventId: event.EventId }).then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => {});
    GetGuestsByEventId({ EventId: event.EventId }).then(d => setGuests(Array.isArray(d) ? d : [])).catch(() => {});
    GetCardTypes({ EventId: event.EventId }).then(d => setCardTypes(Array.isArray(d) ? d : [])).catch(() => {});
  }, [event, canView]);

  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '', [functions]);
  const headcountOf = useCallback((fnId) => (fnId ? functionHeadcount(guests, fnId, cardTypes).persons : 0), [guests, cardTypes]);

  // Persons / total of an expense (row or form values).
  const personsOf = useCallback((e) => (e.PersonsSource === 'Headcount' && e.PersonsFunctionId
    ? headcountOf(e.PersonsFunctionId) : num(e.Persons)), [headcountOf]);
  const totalOf = useCallback((e) => (e.AmountType === 'PerPerson'
    ? round2(num(e.Rate) * personsOf(e)) : num(e.TotalAmount)), [personsOf]);

  const rows = useMemo(() => expenses.map(e => {
    const total = totalOf(e);
    const paid = num(e.PaidAmount);
    return {
      ...e, total, paid, balance: round2(total - paid), persons: personsOf(e),
      payments: payments.filter(p => String(p.ExpenseId) === String(e.ExpenseId)),
    };
  }), [expenses, payments, totalOf, personsOf]);

  const filtered = useMemo(() => {
    let list = rows;
    if (catFilter) list = list.filter(e => e.Category === catFilter);
    if (onlyDue) list = list.filter(e => e.balance > 0);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(e => `${e.Title} ${e.Category} ${e.VendorName || ''} ${e.Notes || ''}`.toLowerCase().includes(s));
    }
    return list;
  }, [rows, catFilter, onlyDue, q]);

  // Grouped by category, in the order of EXPENSE_CATEGORIES then any others.
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach(e => {
      const k = e.Category || 'Other';
      if (!m.has(k)) m.set(k, { category: k, rows: [], total: 0, paid: 0, balance: 0 });
      const g = m.get(k);
      g.rows.push(e); g.total += e.total; g.paid += e.paid; g.balance += e.balance;
    });
    const order = (c) => { const i = EXPENSE_CATEGORIES.indexOf(c); return i < 0 ? 999 : i; };
    return [...m.values()].sort((a, b) => order(a.category) - order(b.category) || a.category.localeCompare(b.category));
  }, [filtered]);

  const totals = useMemo(() => rows.reduce((t, e) => ({
    total: t.total + e.total, paid: t.paid + e.paid, balance: t.balance + e.balance,
    estimates: t.estimates + (Number(e.IsEstimate) === 1 ? 1 : 0), count: t.count + 1,
  }), { total: 0, paid: 0, balance: 0, estimates: 0, count: 0 }), [rows]);

  const categories = useMemo(() => [...new Set([...EXPENSE_CATEGORIES, ...expenses.map(e => e.Category).filter(Boolean)])], [expenses]);
  const paidByOptions = useMemo(() => [...new Set([myName, ...payments.map(p => p.PaidBy)].filter(Boolean))].sort(), [payments, myName]);

  // ── Expense add / edit / delete ──────────────────────────────────────────
  const openAdd = (category = '') => setForm({ ...blankExpense(), Category: category || catFilter || '' });
  const openEdit = (e) => setForm({
    ExpenseId: e.ExpenseId, Category: e.Category || '', Title: e.Title || '', FunctionId: e.FunctionId ? String(e.FunctionId) : '',
    VendorName: e.VendorName || '', VendorMobile: e.VendorMobile || '', AmountType: e.AmountType || 'Fixed',
    Rate: e.Rate ? String(Number(e.Rate)) : '', Persons: e.Persons ? String(e.Persons) : '',
    PersonsSource: e.PersonsSource || 'Manual', PersonsFunctionId: e.PersonsFunctionId ? String(e.PersonsFunctionId) : '',
    TotalAmount: e.TotalAmount ? String(Number(e.TotalAmount)) : '', IsEstimate: Number(e.IsEstimate) === 1 ? 1 : 0, Notes: e.Notes || '',
  });
  const closeForm = () => { if (!saving) setForm(null); };
  const setF = (k, v) => setForm(f => {
    let val = v;
    if (k === 'Title' || k === 'VendorName') val = normaliseText(v);
    if (k === 'VendorMobile') val = tenDigits(v);
    if (['Rate', 'TotalAmount'].includes(k)) val = String(v).replace(/[^\d.]/g, '');
    if (k === 'Persons') val = String(v).replace(/\D/g, '');
    const next = { ...f, [k]: val };
    // The food of a function: default the headcount to that function.
    if (k === 'FunctionId' && val && !f.PersonsFunctionId) next.PersonsFunctionId = val;
    return next;
  });
  const formTotal = form ? totalOf(form) : 0;
  const formPersons = form ? personsOf(form) : 0;

  const saveExpense = async () => {
    const f = form;
    if (!String(f.Category).trim()) { toast('Choose a category'); return; }
    if (!String(f.Title).trim()) { toast('What is this expense for?'); return; }
    if (f.AmountType === 'PerPerson' && !num(f.Rate)) { toast('Enter the rate per person'); return; }
    if (f.AmountType === 'PerPerson' && f.PersonsSource === 'Headcount' && !f.PersonsFunctionId) { toast('Which function’s headcount?'); return; }
    if (f.VendorMobile && !/^\d{10}$/.test(f.VendorMobile)) { toast('Vendor mobile must be 10 digits'); return; }
    const data = {
      Category: normaliseText(f.Category).trim(), Title: f.Title.trim(), FunctionId: f.FunctionId ? Number(f.FunctionId) : null,
      VendorName: f.VendorName.trim() || null, VendorMobile: f.VendorMobile || null, AmountType: f.AmountType,
      Rate: f.AmountType === 'PerPerson' ? num(f.Rate) : 0,
      Persons: f.AmountType === 'PerPerson' && f.PersonsSource === 'Manual' ? num(f.Persons) : 0,
      PersonsSource: f.AmountType === 'PerPerson' ? f.PersonsSource : 'Manual',
      PersonsFunctionId: f.AmountType === 'PerPerson' && f.PersonsSource === 'Headcount' ? Number(f.PersonsFunctionId) : null,
      // A per-person total is stored as computed now (the list recomputes it live).
      TotalAmount: totalOf(f), IsEstimate: f.IsEstimate ? 1 : 0, Notes: f.Notes.trim() || null, UpdatedBy: me,
    };
    setSaving(true);
    try {
      if (f.ExpenseId) await UpdateExpense({ data, where: `ExpenseId = ${Number(f.ExpenseId)}` });
      else await AddExpense({ ...data, EventId: event.EventId, OrganiserId: event.OrganiserId, Valid: 1, CreatedBy: me });
      toast('Expense saved'); setForm(null); load();
    } catch { toast('Could not save the expense'); } finally { setSaving(false); }
  };

  const deleteExpense = async (e) => {
    if (!window.confirm(`Delete "${e.Title}"${e.payments.length ? ` and its ${e.payments.length} payment(s)` : ''}?`)) return;
    try {
      await UpdateExpense({ data: { Valid: 0, UpdatedBy: me }, where: `ExpenseId = ${Number(e.ExpenseId)}` });
      if (e.payments.length) await UpdateExpensePayment({ data: { Valid: 0, UpdatedBy: me }, where: `ExpenseId = ${Number(e.ExpenseId)}` });
      toast('Deleted'); load();
    } catch { toast('Could not delete'); }
  };

  // ── Payments ─────────────────────────────────────────────────────────────
  const toggleOpen = (e) => setOpenId(id => (id === e.ExpenseId ? null : e.ExpenseId));
  const openPayAdd = (e) => {
    setOpenId(e.ExpenseId);
    // Default amount = what is still due.
    setPayForm({ ...blankPayment(e.ExpenseId, myName), Amount: e.balance > 0 ? String(e.balance) : '' });
  };
  const openPayEdit = (p) => setPayForm({
    ExpensePaymentId: p.ExpensePaymentId, ExpenseId: p.ExpenseId, PaymentDate: String(p.PaymentDate || '').slice(0, 10),
    Amount: String(Number(p.Amount || 0)), PaidBy: p.PaidBy || '', PaymentMode: p.PaymentMode || 'Cash', Notes: p.Notes || '',
  });
  const closePay = () => { if (!saving) setPayForm(null); };
  const setPF = (k, v) => setPayForm(f => ({
    ...f, [k]: k === 'Amount' ? String(v).replace(/[^\d.]/g, '') : k === 'PaidBy' ? normaliseText(v) : v,
  }));
  const savePayment = async () => {
    const f = payForm;
    if (!f.PaymentDate) { toast('Pick the payment date'); return; }
    if (!(num(f.Amount) > 0)) { toast('Enter the amount'); return; }
    const data = {
      PaymentDate: f.PaymentDate, Amount: num(f.Amount), PaidBy: String(f.PaidBy).trim() || null,
      PaymentMode: f.PaymentMode || null, Notes: String(f.Notes).trim() || null, UpdatedBy: me,
    };
    setSaving(true);
    try {
      if (f.ExpensePaymentId) await UpdateExpensePayment({ data, where: `ExpensePaymentId = ${Number(f.ExpensePaymentId)}` });
      else await AddExpensePayment({ ...data, ExpenseId: Number(f.ExpenseId), EventId: event.EventId, Valid: 1, CreatedBy: me });
      toast('Payment saved'); setPayForm(null); load();
    } catch { toast('Could not save the payment'); } finally { setSaving(false); }
  };
  const deletePayment = async (p) => {
    if (!window.confirm(`Delete the payment of ₹${Number(p.Amount).toLocaleString('en-IN')}?`)) return;
    try {
      await UpdateExpensePayment({ data: { Valid: 0, UpdatedBy: me }, where: `ExpensePaymentId = ${Number(p.ExpensePaymentId)}` });
      toast('Payment deleted'); load();
    } catch { toast('Could not delete'); }
  };

  return {
    canView, canAdd, canEdit, canDelete, loading, event,
    rows, filtered, groups, totals, categories, functions, fnName, headcountOf, paidByOptions,
    q, setQ, catFilter, setCatFilter, onlyDue, setOnlyDue,
    openId, toggleOpen,
    form, formTotal, formPersons, openAdd, openEdit, closeForm, setF, saveExpense, deleteExpense,
    payForm, openPayAdd, openPayEdit, closePay, setPF, savePayment, deletePayment, saving,
  };
}
